const Imageinfo = require('./imageinfo');
const IfWP = require('./iframe-worker-pool');
const StorageApiClient = require('./storage-api-client');
const Utils = require('./utils');
const Queue = require('./queue');
const Exif = require('./exif');
const FileValidation = require('./file-validation');
const CancellationToken = require('./CancellationToken');
const CancelUploadError = require('./CancelUploadError');
const XServerApiClient = require('./xserver-api-client');
const ThumbnailGenerator = require('./thumbnail-generator');
const Profiling = require('./profiling');

const ErrorCodes = {
    MAX_FILE_SIZE: 'MAX_FILE_SIZE',
    MAX_PHOTO_RESOLUTION: 'MAX_PHOTO_RESOLUTION',
    UPLOAD_FAILED: 'UPLOAD_FAILED',
    REGISTRATION_FAILED: 'REGISTRATION_FAILED',
    FILE_EXISTS_CHECK_FAILED: 'FILE_EXISTS_CHECK_FAILED',
    GET_UPLOAD_URL_FAILED: 'GET_UPLOAD_URL_FAILED',
    GET_IMAGE_DATA_FAILED: 'GET_IMAGE_DATA_FAILED',
    UNSUPPORTED_FILE_TYPE: 'UNSUPPORTED_FILE_TYPE',
    COMPLETE_UPLOAD_FAILED: 'COMPLETE_UPLOAD_FAILED',
    GENERATE_MD5CHECKSUM_FAILED: 'GENERATE_MD5CHECKSUM_FAILED',
    CANCELLED: 'CANCELLED',
    INVALID_IMAGE: 'INVALID_IMAGE',
    DUPLICATE: 'DUPLICATE',
    DUPLICATE_MD5_IN_FILES_ADDED: 'DUPLICATE_MD5_IN_FILES_ADDED',
    PROGRESSIVE_JPEG: 'PROGRESSIVE_JPEG',
    EMPTY_FILE: 'EMPTY_FILE',
    ALREADY_UPLOADED: 'ALREADY_UPLOADED',
    STORAGE_CORE_RETRIEVE_FAILED: 'STORAGE_CORE_RETRIEVE_FAILED'
};

const WarningCodes = {
    READ_EXIF_FAILED: 'READ_EXIF_FAILED'
};

// Time value of interval for storage core data retrieval after upload
const STORAGE_CORE_DATA_INTERVAL_TIME = 1000;

// Number of attempts to try to get storage core data before failing
const STORAGE_CORE_DATA_ATTEMPTS = 15;

// Default parallel uploads
const DEFAULT_PARALLEL_UPLOADS = 7;

/**
 * Uploader
 * This is the main class
 */
class Uploader {

    /**
     * Constructor
     * @param {Object} data
     */
    constructor(data) {
        if (Utils.emptyObjectProperty(data, "parallellUploads")) {
            throw "Uploader.constructor: missing parallellUploads";
        }
        if (Utils.emptyObjectProperty(data, "iframeBasePath")) {
            throw "Uploader.constructor: missing iframeBasePath";
        }

        if (Utils.emptyObjectProperty(data, "allowedMimeTypes")) {
            throw "Uploader.constructor: missing allowedMimeTypes";
        }

        if (data.token && data.authenticator) {
            throw "Uploader.constructor: you are not allowed to have token and authenticator at same time"
        }

        // Calculate the parallel uploads
        if (data.parallellUploads === "auto") {
            data.parallellUploads = DEFAULT_PARALLEL_UPLOADS;
        }

        if (data.hasOwnProperty("allowUploadProgressiveJpeg")) {
            this.allowUploadProgressiveJpeg = data.allowUploadProgressiveJpeg;
        } else {
            this.allowUploadProgressiveJpeg = true;
        }

        this.waitForServerSideThumbnail = data.hasOwnProperty("waitForServerSideThumbnail") && data.waitForServerSideThumbnail;

        this.parallellUploads = data.parallellUploads;
        this.iframeBasePath = data.iframeBasePath;
        this.uploadIfDuplicate = data.uploadIfDuplicate;
        this.allowedMimeTypes = data.allowedMimeTypes;
        this.maxFileSize = data.maxFileSize;
        this.maxPhotoPixels = data.maxPhotoPixels;
        this.ajaxRetryOptions = data.retry;
        this.validation = Object.assign({ enabled: true, config: {} }, data.validation ? data.validation : {});

        // Profiling the application if passed in config
        this.profiling = new Profiling(data.hasOwnProperty("profile") && data.profile === true);

        this.uploadQueueProcessor = new Queue(
            data.parallellUploads,
            this._fileUploaded.bind(this),
            this._fileUploadFailed.bind(this)
        );

        if (data.uploadToXServer === true) {
            if (Utils.emptyObjectProperty(data, "imageEditServerUrl")) {
                throw "Uploader.constructor: missing imageEditServerUrl";
            }
            this.imageEditServerUrl = data.imageEditServerUrl;
            this.uploadToXServer = true;
            this.xserverApiClient = new XServerApiClient(data.xserverApiUrl, data.retry, data.s3UrlMapperRegex);
        } else {
            this.uploadToXServer = false;
        }

        this.iframeMD5WorkerPool = new IfWP(this.iframeBasePath +
            "/md5worker.html", data.parallellUploads, this.profiling,
            "generateMD5"); //Using iframes
        //this.iframeMD5WorkerPool = new WorkerPool(this.iframeBasePath + "/md5worker.js", data.parallellUploads, this.profiling, "generateMD5"); //Using web workers
        this.thumbGen = new ThumbnailGenerator(data.parallellUploads, this.iframeBasePath, this.profiling, "generateThumb");
        this.exif = Exif;

        this.uploadStartEventListeners = [];
        this.uploadCompleteEventListeners = [];
        this.uploadDoneEventListeners = [];
        this.uploadErrorEventListeners = [];
        this.uploadCanceledEventListeners = [];
        this.statisticsEventListeners = [];
        this.fileRegistrationEventListeners = [];
        this.fileAddedEventListeners = [];
        this.storageCoreDataRecievedListeners = [];
        this.fileUploadProgressEventListeners = [];
        this.fileUploadRetryEventListeners = []
        this.authFailedEventListeners = [];
        this.fileAddedMultipleTimesEventListeners = [];
        this.uploadWarningEventListeners = [];
        this.imageDimensionsEventListeners = [];

        this.storageApiClient = new StorageApiClient({
            applicationName: data.applicationName,
            applicationVersion: data.applicationVersion,
            storageApiUrl: data.storageApiUrl,
            sessionId: data.sessionId,
            uploadIfDuplicate: data.uploadIfDuplicate,
            returnDimensions: data.returnDimensions,
            token: data.token,
            authenticator: data.authenticator,
            channel: data.channel,
            ajaxRetryOptions: data.retry,
            s3UrlMapperRegex: data.s3UrlMapperRegex,
            authFailedEventListeners: this.authFailedEventListeners
        });

        this.reset();
    }

    /**
     * Reset all data in the uploader except configuration.
     * To avoid bugs this can only be called after upload is done
     */
    reset() {
        if (this.uploadDone || typeof this.uploadDone === "undefined") {
            this.filesRegistered = [];
            this.filesAdded = [];
            this.filesUploaded = 0;
            this.filesFailed = 0;
            this.filesFailedOnStorageCore = 0;
            this.filesProcessed = new Set();
            this.uploadDone = false;
            this._updateStatisticsListeners();
        }
    }

    /**
     * Get a callback each time a file uploaded is actually started
     * The callback will get the file object as parameter
     */
    uploadStartEvent(callback) {
        this.uploadStartEventListeners.push(callback);
    }

    /**
     * Remove the callback from the subscribtion to the uploadStartEvent
     */
    unsubscribeUploadStartEvent(callback) {
        this.uploadStartEventListeners = this.uploadStartEventListeners.filter (function (item) {
            return item !== callback;
        });
    }

    /**
     * Get a callback each time a file is uploaded
     * The callback will get the file object as parameter
     */
    uploadCompleteEvent(callback) {
        this.uploadCompleteEventListeners.push(callback);
    }

    /**
     * Remove the callback from the subscribtion to the uploadCompleteEvent
     */
    unsubscribeUploadCompleteEvent(callback) {
        this.uploadCompleteEventListeners = this.uploadCompleteEventListeners.filter (function (item) {
            return item !== callback;
        });
    }

    /**
     * Get a callback each time a file is added
     */
    fileAddedEvent(callback) {
        this.fileAddedEventListeners.push(callback);
    }

    /**
     * Remove the callback from the subscribtion to the fileAddedEvent
     */
    unsubscribeFileAddedEvent(callback) {
        this.fileAddedEventListeners = this.fileAddedEventListeners.filter (function (item) {
            return item !== callback;
        });
    }

    /**
     * Get a callback each time a file is added twice or more in the same round of upload.
     * These files are ignored cause they are already added, but the app might want to know about it.
     */
    fileAddedMultipleTimesEvent(callback) {
        this.fileAddedMultipleTimesEventListeners.push(callback);
    }

    /**
     * Remove the callback from the subscribtion to the fileAddedMultipleTimesEvent
     */
    unsubscribeFileAddedMultipleTimesEvent(callback) {
        this.fileAddedMultipleTimesEventListeners = this.fileAddedMultipleTimesEventListeners.filter (function (item) {
            return item !== callback;
        });
    }


    /**
     * Get a callback each time a file fails for some reason
     * The callback will get the file object as parameter, and
     * the error as the second parameter
     */
    uploadErrorEvent(callback) {
        this.uploadErrorEventListeners.push(callback);
    }

    /**
     * Remove the callback from the subscribtion to the uploadErrorEvent
     */
    unsubscribeUploadErrorEvent(callback) {
        this.uploadErrorEventListeners = this.uploadErrorEventListeners.filter (function (item) {
            return item !== callback;
        });
    }

    /**
     * Get a callback each time a file upload gets canceled
     * The callback will get the file object as parameter
     */
    uploadCanceledEvent(callback) {
        this.uploadCanceledEventListeners.push(callback);
    }

    /**
     * Remove the callback from the subscribtion to the uploadCanceledEvent
     */
    unsubscribeUploadCanceledEvent(callback) {
        this.uploadCanceledEventListeners = this.uploadCanceledEventListeners.filter (function (item) {
            return item !== callback;
        });
    }

    /**
     * Get a callback each time upload statistics changes
     */
    statisticsUpdatedEvent(callback) {
        this.statisticsEventListeners.push(callback);
    }

    /**
     * Remove the callback from the subscribtion to the statisticsUpdatedEvent
     */
    unsubscribeStatisticsUpdatedEvent(callback) {
        this.statisticsEventListeners = this.statisticsEventListeners.filter (function (item) {
            return item !== callback;
        });
    }

    /**
     * Get a callback when all files have been uploaded
     */
    doneUploadingEvent(callback) {
        this.uploadDoneEventListeners.push(callback);
    }

    /**
     * Remove the callback from the subscribtion to the doneUploadingEvent
     */
    unsubscribeDoneUploadingEvent(callback) {
        this.uploadDoneEventListeners = this.uploadDoneEventListeners.filter (function (item) {
            return item !== callback;
        });
    }

    /**
     * Get a callback when a file has been registered and has been given an ID
     * The callback function will get the file object as a parameter.
     * The file object will have a regId property and an alreadyUploaded property
     */
    fileRegisteredEvent(callback) {
        this.fileRegistrationEventListeners.push(callback);
    }

    /**
     * Remove the callback from the subscribtion to the fileRegisteredEvent
     */
    unsubscribeFileRegisteredEvent(callback) {
        this.fileRegistrationEventListeners = this.fileRegistrationEventListeners.filter (function (item) {
            return item !== callback;
        });
    }

    /**
     * Get a callback when we have fetched data from storage core api about an uploaded file
     * The data includes server generated thumbnails
     */
    storageCoreDataRecievedEvent(callback) {
        this.storageCoreDataRecievedListeners.push(callback);
    }

    /**
     * Remove the callback from the subscribtion to the storageCoreDataRecievedEvent
     */
    unsubscribeStorageCoreDataRecievedEvent(callback) {
        this.storageCoreDataRecievedListeners = this.storageCoreDataRecievedListeners.filter (function (item) {
            return item !== callback;
        });
    }

    /**
     * Get a callback each time there is progress on an upload
     * It will report % of upload for the file
     * First parameter for callback will be the file object, second will be the percent
     */
    fileUploadProgressEvent(callback) {
        this.fileUploadProgressEventListeners.push(callback);
    }

    /**
     * Remove the callback from the subscribtion to the fileUploadProgressEvent
     */
    unsubscribeFileUploadProgressEvent(callback) {
        this.fileUploadProgressEventListeners = this.fileUploadProgressEventListeners.filter (function (item) {
            return item !== callback;
        });
    }

    /**
     * Get a callback each time uploader tries to retry request
     * First parameter for callback will be the file object, second will be the number of retry in a row
     */
    fileUploadRetryEvent(callback) {
        this.fileUploadRetryEventListeners.push(callback);
    }

    /**
     * Remove the callback from the subscribtion to the fileUploadRetryEvent
     */
    unsubscribeFileUploadRetryEvent(callback) {
        this.fileUploadRetryEventListeners = this.fileUploadRetryEventListeners.filter (function (item) {
            return item !== callback;
        });
    }

    /**
     * A request to the storage backend has failed with HTTP 401
     * The client needs to reset the authenticator for the next requests to succeed
     */
    authenticationFailedEvent(callback) {
        this.authFailedEventListeners.push(callback);
    }

    /**
     * Warnings that does not halt the upload can be subribed to here
     */
     uploadWarningEvent(callback) {
         this.uploadWarningEventListeners.push(callback);
     }

     /**
      * Remove the callback from the subscribtion to the uploadWarningEvent
      */
     unsubscribeUploadWarningEvent(callback) {
         this.uploadWarningEventListeners = this.uploadWarningEventListeners.filter (item => {
             item !== callback;
         });
     }

     /**
      * Get notified when the image dimensions of an image is aquired
      */
     imageDimensionsEvent(callback) {
         this.imageDimensionsEventListeners.push(callback);
     }

     /**
      * Remove the callback from the subscription to the imageDimensionsEvent
      */
     unsubscribeImageDimensionsEvent(callback) {
         this.imageDimensionsEventListeners = this.imageDimensionsEventListeners.filter (item => {
             item != callback
         })
     }


    /**
     * Update the token (if you have a refreshed token or if the user just logged in)
     */
    updateToken(token) {
        if (!this.uploadToXServer) {
            this.storageApiClient.updateToken(token);
        }
    }

    updateAuthenticator(authenticator) {
        if (!this.uploadToXServer) {
            this.storageApiClient.updateAuthenticator(authenticator);
        }
    }

    /**
     * Link all anonymous photos by session id.
     */
    linkAllAnonymousPhotosBySessionId() {
        return this.storageApiClient.linkAllAnonymousPhotosBySessionId()
            .then(() => {
                // Change photos to be authenticated.
                this.filesRegistered.forEach(file => {
                    file.uploadedAnonymously = false;
                });
            });
    }

    /**
     * Pause the upload
     */
    togglePauseUpload() {
        this.uploadQueueProcessor.togglePause();
    }

    /**
     * Cancel uploads for the given local IDs
     */
    cancelUploads(localIdList) {
        this.filesAdded.map(function(item) {
            if (localIdList.indexOf(item.localId) !== -1) {
                item.cancel();
            }
        });
    }

    /**
     *  Cancel all uploads
     */
    cancelAllUploads(soft = false) {
        const itemsToCancel = soft
            ? this.filesAdded.filter(file => !this.filesProcessed.has(file.localId))
            : this.filesAdded.slice();

        itemsToCancel.forEach(item => item.cancel());
    }

    /**
     * Add list of files to uploader
     * @deprecated Use uploadFile() an and addFile()
     */
    addFiles(fileList) {
        this.profiling.start("uploadDone", null);

        for (let i = 0; i < fileList.length; i++) {
            this.addFile(fileList[i]);
        }
    }

    /**
     * Preare a file for upload
     * Calculates checksum and verifies image format and integrity
     * Rejects duplicate files, including ones uploaded in previous sessions
     * @param {File} file
     * @returns {Promise<File>} the validated file object with metadata fields
     */
    importFile(file) {
        if (!file.localId) {
            file.localId = this._getLocalId(file); // LEGACY
        }

        const ct = new CancellationToken('fileImport');
        file.cancelled = false;
        file.cancelTokenList = [{
            name: ct.getOperationName(),
            cancelToken: ct
        }];

        file.cancel = () => {
            for (var i = 0; i < file.cancelTokenList.length; i++) {
                file.cancelTokenList[i].cancelToken.cancel();
            }
            file.cancelled = true;
            this._updateUploadCanceledListeners(file);
            this._discardFile(file);
        };

        return this._validateFile(file)
            .then(file => {
                ct.throwIfCanceled();
                this._throwIfEmptyFile(file);
                return this._getFileData(file)
            })
            .then(file => {
                ct.throwIfCanceled();
                this._throwIfEmptyFile(file);
                return this._applyDimensionsToImage(file)
            })
            .then(file => {
                ct.throwIfCanceled();
                this._throwIfEmptyFile(file);
                this._throwIfExceedsMaxResolution(file);
                return this._applyMd5ChecksumAndValidateFileContents(file)
            })
            .then(file => {
                ct.throwIfCanceled();
                this._throwIfEmptyFile(file);
                if (this.uploadIfDuplicate) {
                    return file
                }

                return this._lookupRemoteIdByFileMD5Sum(file)
                    .then(remoteId => {
                        if (remoteId) {
                            return Promise.reject({ code: ErrorCodes.ALREADY_UPLOADED, remoteId })
                        }
                        return file
                    })
            })
    }

    /**
     * Query StorageCore for exsiting remote items with the specified base64md5Checksum
     * @param {File} file file to pass through in a case of failure
     * @returns {Promise<string?>} remote Id of a StorageCore item, if exists
     */
    _lookupRemoteIdByFileMD5Sum(file) {
        return this.storageApiClient.fileExists('ignored', file.base64md5Checksum)
            .then(response => {
                if (response !== false) {
                    return response.registrationId
                }
            }).catch(error => {
                Promise.reject({
                    error: error,
                    code: ErrorCodes.FILE_EXISTS_CHECK_FAILED,
                    file: file,
                    message: `We’re sorry: we couldn’t registrer file ‘${file.name}’, so we’ve removed it from your order. This might be a temporary error and you could try uploading it again`
                });
            });
    }

    /**
     * Upload a file (with metadata from importFile)
     * @param {ImportedFile} file
     * @returns {Promise<UploadedFile>} file with regId and StorageCore data
     */
    uploadFile(file) {
        const ct = file.cancelTokenList[0].cancelToken

        this._updateFileAddedListeners(file); // Legacy, to keep old listeners working

        return Promise.resolve(file)
            .then(file => {
                ct.throwIfCanceled();
                this._throwIfEmptyFile(file);
                return this._registerImage(file)
            })
            .then(file => {
                ct.throwIfCanceled();
                this._throwIfEmptyFile(file);
                return this._applyUploadUrl(file)
            })
            .catch(error => {
                // Catch "already registered" and recover by resolving an existing photo
                if (!file.regId && error.error && error.error.status === 409) {
                    return this._lookupRemoteIdByFileMD5Sum(file)
                        .then(remoteId => {
                            if (!remoteId) throw new Error('Remote item disappeared. Concurrent uploads?')
                            file.regId = remoteId
                            return this.resolvePhotoIDs([remoteId])
                        })
                        .then(results => {
                            file.storageCoreData = results[0]
                            return file
                        })
                }

                // Trigger file registration error callback
                this._fileRegistrationFailed({ file, error });

                // Bubble up error to main handler
                return Promise.reject(error);
            })
            .then(file => {
                ct.throwIfCanceled();
                this._throwIfEmptyFile(file);
                return this._waitForStorageCoreData(file);
            })
    }

    /**
     * Wait until the file upload is complete and StorageCore data is received
     * @param {File} file
     * @returns {Promise<File>}
     */
    _waitForStorageCoreData(file) {
        if (file.storageCoreData) {
            return Promise.resolve(file);
        }

        let coreDataCb
        let uploadErrorCb
        let cancellationCb

        return new Promise((resolve, reject) => {
            coreDataCb = (targetFile) => targetFile == file && resolve(file)
            uploadErrorCb = (targetFile, error) => targetFile == file && reject(error)
            cancellationCb = (targetFile) => targetFile == file && reject(new CancelUploadError())
            this.storageCoreDataRecievedEvent(coreDataCb)
            this.uploadErrorEvent(uploadErrorCb)
            this.uploadCanceledEvent(cancellationCb)
            this._fileRegistered(file)
        })
        .finally(() => {
            this.unsubscribeStorageCoreDataRecievedEvent(coreDataCb)
            this.unsubscribeStorageCoreDataRecievedEvent(uploadErrorCb)
            this.unsubscribeUploadCanceledEvent(cancellationCb)
        })
    }

    /**
     * Add one file to uploader
     * @deprecated Use importFile() and uploadFile()
     */
    addFile(file) {

        this.uploadDone = false;

        const localId = this._getLocalId(file);

        const exists = this.filesAdded.find(item => item.localId === localId);

        file.localId = localId;
        if (exists) {
            // Check if file now has higher priority
            if (file.highPriority && !exists.highPriority) {
                this.uploadQueueProcessor.prioritizeTask(localId);
            }
            this._updateFileAddedMultipleTimesListeners(exists);
            return;
        }

        this.filesAdded.push(file);

        this.importFile(file)
            .catch(error => {
                // Handle errors
                this._fileRegistrationFailed({ file, error });
                
                // Bubble up error so upload is never invoked
                Promise.reject(error);
            })
            .then(file => this.uploadFile(file))
            .catch(error => { }) // All the errors are already handled inside

        this._updateFileAddedListeners(file);
        this._updateStatisticsListeners();
    }

    /**
     * Remove the specified file form all file lists
     * @private
     * @param {File} file
     */
    _discardFile(file) {
        const indexAdded = this.filesAdded.indexOf(file);
        if (indexAdded !== -1) {
            this.filesAdded.splice(indexAdded,1);
        }
        const indexRegistered = this.filesRegistered.indexOf(file);
        if (indexRegistered !== -1) {
            this.filesRegistered.splice(indexRegistered,1);
        }
    }

    /**
     * Returns a promise that awaits update of the storage core data
     * Rejects if timeout is reached; timeout of <= 0 will wait until resolved or error
     * @private
     * @param {File} file
     * @param {number=} optTimeoutMs
     */
    _getStorageCoreData(file, optTimeoutMs = 5000) {
        if (file.hasOwnProperty("storageCoreData")) {
            return Promise.resolve(file["storageCoreData"]);
        }
        return new Promise((resolve, reject) => {
            let timeout;
            const subscription = (updatedFile) => {
                if (updatedFile !== file) return;
                unsubscriber();
                resolve(updatedFile["storageCoreData"]);
            }
            const rejection = (updatedFile, reason) => {
                if (updatedFile !== file) return;
                unsubscriber();
                reject(reason);
            }
            const unsubscriber = () => {
                clearTimeout(timeout);
                this.unsubscribeStorageCoreDataRecievedEvent(subscription);
                this.unsubscribeUploadErrorEvent(rejection);
            }
            // Set up an operation timeout
            if (optTimeoutMs > 0) {
                timeout = setTimeout(() => { rejection(file, "timeout"); }, optTimeoutMs);
            }
            // subscribe for the event
            this.storageCoreDataRecievedEvent(subscription);
            this.uploadErrorEvent(rejection);
        });
    }

    /**
     * Get metadata of remote photos by IDs
     *
     * WARNING The order of the results is not guaranteed to match the input array order
     *
     * @param {string[]} photoIds IDs of remote photos
     * @returns {Promise<Object>} photo metadata
     */
    resolvePhotoIDs(photoIds) {
        // Currently xServer photos cannot be resolved though the authenticated endpoint
        // thus we partition the IDs and resolve them separately
        const [xServerIds, mhnIds] = Utils.partition(photoIds, id => id.indexOf('-') !== -1)

        const getUploads = (ids, anonymous) => this.storageApiClient.getUploadData(ids, anonymous)

        return Promise.all([ getUploads(xServerIds, true), getUploads(mhnIds, !this.storageApiClient.authenticator) ])
            .then(([xServerPhotos, mhnPhotos]) => [...xServerPhotos, ...mhnPhotos])
    }

    /**
     * Get a thumbnail url to the image. Size is fex 300. This will be the maximum size of either with or height
     * This will give you a client generated thumbnail unless the server generated thumbnail is available
     * A timeout of <= 0 will await indefinitely
     * @param {string} localId
     * @param {number} size
     * @param {number=} optTimeoutMs
     * @return {Promise}
     */
    getThumbnail(localId, size, optTimeoutMs = 15000) {
        this.profiling.start("getThumbnail", localId);
        const file = this.filesAdded.find(item => item.localId === localId);

        if (!file) {
            return Promise.reject({
                error: "there is no file added with the local id of: " + localId
            });
        }
        return new Promise((resolve, reject) => {

            if (this.waitForServerSideThumbnail) {
                 //Check if we have some server generated thumbnails available for this image
                this._getStorageCoreData(file, optTimeoutMs)
                .then(storageCoreData => {
                    resolve(this._getStorageCoreDataThumbnail(file, size));
                    this.profiling.end("getThumbnail", localId);
                })
                .catch(reason => {
                    if (reason !== "timeout") {
                        // If it's anything but a timeout, rethrow
                        reject(reason);
                        return;
                    }
                    // Timed out, invoke local generation
                    this._waitForRotationData(file)
                        .then((file) => {
                            this._generateThumbnailLocally(file, size).then((data) => {
                                resolve(data)
                            }).catch((error) => {
                                reject(error);
                            })
                        });
                })
            } else {
                //Check if we have some server generated thumbnails available for this image
                if (file.hasOwnProperty("storageCoreData")) {
                    resolve(this._getStorageCoreDataThumbnail(file, size));
                    this.profiling.end("getThumbnail", localId);
                    return;
                }

                //There was no server generated thumbnail available yet, generate a thumbnail client side
                this._waitForRotationData(file).then((file) => {
                    this._generateThumbnailLocally(file, size).then((data) => {
                        resolve(data)
                    }).catch((error) => {
                        reject(error);
                    })
                });
            }
        });
    }

    /* Private functions */

    /**
     * Generates a thumbnail locally using iframeworker
     * @private
     * @param {File} file
     * @return {Promise}
     */
    _generateThumbnailLocally(file, size) {
        var rotate = 0;
        var flip = 'NONE';
        try {
            rotate = file.filter.rotate.angle;
            flip = file.filter.rotate.flip;
        } catch (e) {

        }

        return this.thumbGen.getThumbnail(file.localId, file, size, rotate, flip);
    }

    /**
     * Based on the files storageCoreData, get thumbnail data for a given size
     * @param {File} file
     * @param {Number} size
     */
    _getStorageCoreDataThumbnail(file, size) {
        const thumb = this._selectThumbnail(file.storageCoreData
            .thumbnails, size);
        let url = thumb.url
        if (this.uploadToXServer) {
            url = this._createFilterRotatedUrl(thumb.url, file.filter);
        }
        return {
            url: thumb.url,
            originalWidth: file.width,
            originalHeight: file.height,
            thumbWidth: thumb.width,
            thumbHeight: thumb.height
        };
    }

    /**
     * If the users computer goes to sleep mode, file references could be lost, and thus the file will be empty
     * @param file
     * @private
     */
    _throwIfEmptyFile(file) {
        if (file.size === 0) {
            throw Error("Empty file");
        }
    }

    /**
     * Check if an image is within the maximum allowed dimensions
     * @param file
     * @private
     */
    _throwIfExceedsMaxResolution(file) {
        const resolution = file.width * file.height;
        if (resolution > this.maxPhotoPixels) {
            throw ({
                file: file,
                code: ErrorCodes.MAX_PHOTO_RESOLUTION,
                pixelSize: resolution,
                maxPhotoPixels: this.maxPhotoPixels,
                message: `The file ‘${file.name}’ has bigger dimensions than the maximum allowed (‘${this.maxPhotoPixels}’)`
            });
        }
    }

    /**
     * Wait for rotation data
     * @private
     * @param file
     * @returns {Promise<any>}
     */
    _waitForRotationData(file) {
        return new Promise(function(resolve, reject) {
            function checkFilter(retryCount) {
                if ((file.hasOwnProperty("filter") && file.filter.hasOwnProperty("rotate")) ||
                    retryCount > 100) {
                    resolve(file);
                } else {
                    setTimeout(function() {
                        checkFilter(retryCount + 1);
                    }.bind(file, resolve, reject), 50);
                }
            }
            checkFilter(0);
        });
    }

    /**
     * Wait until all thumbnails are available
     * URLs for XServer photos thumbnail are generated up-front, before they are actually available
     * Times out after 10 seconds
     * @param {object} thumbnails thumbnail objects from a storageCoreData
     * @returns {Promise}
     */
    _waitForThumbnails(thumbnails) {
        const promises = thumbnails.map(t => Utils.waitUntilAvailable(t.url))
        const watchdog = Utils.delay(10000).then(() => { throw Error('Time out on waiting for XServer thumbnails') })
        return Promise.race([ Promise.all(promises), watchdog ])
    }

    /**
     * Select thumbnail
     * @private
     * @param {Array} thumbnailList
     * @param {number} size
     * @returns {*}
     */
    _selectThumbnail(thumbnailList, size) {
        var thumbnails = thumbnailList.sort(function(a, b) {
            var aSize = a.width > a.height ? a.width : a.height;
            var bSize = b.width > b.height ? b.width : b.height;
            if (aSize < bSize) {
                return 1;
            } else if (aSize > bSize) {
                return -1;
            }
            return 0;
        });

        var selectedThumbnail = null;
        for (var i = 0; i < thumbnails.length; i++) {
            var thumbSize = thumbnails[i].width > thumbnails[i].height ?
                thumbnails[i].width : thumbnails[i].height;
            if (thumbSize > size) {
                selectedThumbnail = thumbnails[i];
            }
        }
        if (selectedThumbnail === null) {
            selectedThumbnail = thumbnails[0];
        }
        return selectedThumbnail;
    }

    /**
     * File registration failed
     * @param {Object} error
     * @private
     */
    _fileRegistrationFailed(error) {
        if (error.error && !error.error.code) {
            if (error.error instanceof CancelUploadError) {
                error.error.code = ErrorCodes.CANCELLED;
            } else {
                error.error.code = ErrorCodes.REGISTRATION_FAILED;
            }
        }

        this._uploadErrorHandler(error.file, error);
    }

    /**
     * File upload failed
     * @param {Object} data
     * @private
     */
    _fileUploadFailed(data) {
        var file = this.filesRegistered.find((item) => item.localId == data.id);
        var error = data.error;
        if (error.error && !error.error.code) {
            error.error.code = ErrorCodes.UPLOAD_FAILED; // FIXME: Unneeded step, research who is using this 
        }

        this._uploadErrorHandler(file, error);
    }

    /**
     * File registered
     * @param file
     * @private
     */
    _fileRegistered(file) {
        // Add to registered files array
        this.filesRegistered.push(file);

        // Update listeners on the event
        this._updateFileRegisteredListeners(file);

        // Don't upload if this was a duplicate and has already been uploaded
        if (file.alreadyUploaded) {
            this._fileUploaded({
                data: file
            });
            return;
        }

        // Add to upload queue
        this.uploadQueueProcessor.addTask({
            id: file.localId,
            promise: () => this._upload(file),
            setProcessed: () => { this.filesProcessed.add(file.localId) }
        }, !!file.highPriority);
    }

    /**
     * File uploaded
     * @param data
     * @private
     */
    _fileUploaded(data) {
        var file = data.data;
        var self = this;
        this._setUploadCompletedStatus(file).then(function(file) {

            //One more file uploaded
            self.filesUploaded += 1;

            //Update listeners on what just happened
            self._updateUploadCompleteListeners(file);
            self._updateStatisticsListeners();
        }).catch(function(error) {
            error.error.code = ErrorCodes.UPLOAD_FAILED; // FIXME: Unneeded step, research who is using this 
            self._uploadErrorHandler(file, error);
        });
    }


    /**
     * This method will register an image before uploading
     *
     * The file object needs to have width, height and name properties with correct values
     *
     * @param file See: https://developer.mozilla.org/en/docs/Web/API/File
     * @return Promise
     * @throws OpeException
     */
    _registerImage(file) {
        var self = this;
        if (file.hasOwnProperty("regId")) {
            return Promise.resolve(file); //File already registered and uploaded
        }
        this.profiling.start("registerFile", file.localId);
        return new Promise(function(resolve, reject) {
            if (self.uploadToXServer) {
                self.xserverApiClient.registerImage(file.name, file.width, file.height)
                    .then(function(data) {
                        file.regId = data.regId;
                        file.uploadData = data;
                        file.uploadedAnonymously = true;
                        self.profiling.end("registerFile", file.localId);
                        resolve(file);
                    }).catch(function(error) {
                        reject({
                            error: error,
                            code: ErrorCodes.REGISTRATION_FAILED,
                            message: `We’re sorry: we couldn’t registrer file ‘${file.name}’, so we’ve removed it from your order. This might be a temporary error and you could try uploading it again`
                        });
                    });
            } else {
                self.storageApiClient.registerFile(file)
                    .then(function(data) {
                        file.regId = data.registrationId;
                        file.uploadedAnonymously = data.uploadedAnonymously;
                        self.profiling.end("registerFile", file.localId);
                        resolve(file);
                    }).catch(function(error) {
                        reject({
                            error: error.error,
                            code: ErrorCodes.REGISTRATION_FAILED,
                            message: `We’re sorry: we couldn’t registrer file ‘${file.name}’, so we’ve removed it from your order. This might be a temporary error and you could try uploading it again`
                        });
                    });
            }
        });
    }

    /**
     * Check if file already exists
     * @param file
     * @returns {Promise<any>}
     * @private
     */
    _checkIfFileAlreadyExists(file) {
        var self = this;

        if (this.uploadIfDuplicate) { //No need to check if it already exists if we're going to upload a duplicate anyway
            return Promise.resolve(file);
        }
        if (this.uploadToXServer) { //XServer doesn't have duplicate checks anyway
            return Promise.resolve(file);
        }
        self.profiling.start("checkIfFileExists", +file.localId);
        return new Promise(function(resolve, reject) {
            self.storageApiClient.fileExists(file.name, file.base64md5Checksum)
                .then(function(data) {
                    if (data !== false) {
                        file.regId = data.registrationId;
                        file.uploadedAnonymously = data.uploadedAnonymously;
                        file.alreadyUploaded = true;

                        //Get data on the file
                        self.storageApiClient.getUploadData([file.regId], file.uploadedAnonymously).then(
                            function(fileList) {
                                file.storageCoreData = fileList[0];
                                self._updateStorageCoreDataRecievedListeners(file);
                                self.profiling.end("checkIfFileExists", + file.localId);
                                resolve(file);
                            }).catch(function(error) {
                            reject({
                                error: error,
                                code: ErrorCodes.FILE_EXISTS_CHECK_FAILED,
                                message: `We’re sorry: we couldn’t registrer file ‘${file.name}’, so we’ve removed it from your order. This might be a temporary error and you could try uploading it again`
                            });
                        })
                    } else {
                        self.profiling.end("checkIfFileExists", + file.localId);
                        resolve(file);
                    }
                }).catch(function(error) {
                    reject({
                        error: error,
                        code: ErrorCodes.FILE_EXISTS_CHECK_FAILED,
                        message: `We’re sorry: we couldn’t registrer file ‘${file.name}’, so we’ve removed it from your order. This might be a temporary error and you could try uploading it again`
                    });
                });
        });
    }

    /**
     * Get Local file Id
     * @param file
     * @returns {string}
     * @private
     */
    _getLocalId(file) {
        return btoa(unescape(encodeURIComponent(file.name)) + file.type + file.size);
    }

    /**
     * Calculate md5 checksum for the image and also validate file contents.
     * Ideally this should be two separate functions, but since we already load the content of the file for generating
     * checksums, it is convenient to validate the file contents at the same time to
     * look for corrupted data without loading the whole file twice.
     */
    _applyMd5ChecksumAndValidateFileContents(file) {
        var self = this;
        return new Promise(function(resolve, reject) {
            var handle = self.iframeMD5WorkerPool.executeJob(file.localId, {
                url: URL.createObjectURL(file),
                validateImage: self.validation.enabled && self._isImageMimeType(file),
                validateProgressiveJpeg: !self.allowUploadProgressiveJpeg,
                imageValidationConfig: self.validation.config
            });

            file.cancelTokenList.push({
                name: handle.cancelToken.getOperationName(),
                cancelToken: handle.cancelToken
            });

            handle.promise.then(function(result) {

                //Check if the hash is equal to the hash of an empty file
                if (result.hash === '1B2M2Y8AsgTpgAmY7PhCfg==') {
                    reject({
                        file: file,
                        error: "Empty file checksum detected",
                        code: ErrorCodes.GENERATE_MD5CHECKSUM_FAILED
                    });
                }
                else if (self.filesAdded.find((item) =>
                        item.base64md5Checksum === result.hash
                    ) && !self.uploadIfDuplicate) { //Already exists
                    reject({
                        file: file,
                        error: "Another file has already been added to the uploader that has the same MD5 checksum as this file",
                        code: ErrorCodes.DUPLICATE_MD5_IN_FILES_ADDED
                    });
                }
                else {
                    file.base64md5Checksum = result.hash;
                    resolve(file);
                }
            }).catch(function(error) {
                if (error.includes('Not a valid image file') && error.includes("Progressive JPEG")) {
                    reject({
                        error: error,
                        code: ErrorCodes.PROGRESSIVE_JPEG
                    });
                }
                else if (error.includes('Not a valid image file')) {
                    reject({
                        error: error,
                        code: ErrorCodes.INVALID_IMAGE
                    });
                }
                else {
                    reject({
                        error: error,
                        code: (error === 'cancelled' ?
                            ErrorCodes.CANCELLED :
                            ErrorCodes.GENERATE_MD5CHECKSUM_FAILED
                        )
                    });
                }
                // FIXME Lost errors here?
            });
        });
    }

    /**
     * After the image has been registered, we need to do a second request
     * to get the upload url, that is, the url to upload the image to.
     * @param file
     * @returns {Promise<any>}
     * @private
     */
    _applyUploadUrl(file) {
        var self = this;

        if (this.uploadToXServer) {
            return Promise.resolve(file); //Upload url is already there because we got it during registration
        }

        self.profiling.start("applyUploadUrl", file.localId);

        return new Promise(function(resolve, reject) {
            self.storageApiClient.getUploadUrl(file.regId, file.base64md5Checksum, file.uploadedAnonymously)
                .then(function(uploadData) {
                    file.uploadData = uploadData;
                    self.profiling.end("applyUploadUrl", file.localId);
                    resolve(file);
                }).catch(function(error) {
                    reject({
                        error: error.error,
                        code: ErrorCodes.GET_UPLOAD_URL_FAILED,
                        message: `We’re sorry: we couldn’t registrer file ‘${file.name}’, so we’ve removed it from your order. This might be a temporary error and you could try uploading it again`
                    });
                });
        })
    }

    /**
     * ASYNC function
     * Get height and width of image
     * @param file
     * @return Promise which then gives the file back with new properities width and height
     */
    _applyDimensionsToImage(file) {
        if (file.hasOwnProperty("width") && file.hasOwnProperty("height")) {
            return Promise.resolve(file); //Usually this will happen because we already got the dimensions from the header
        }
        else {
            var self = this;
            file.discardImage = true;

            return this._readImage(file)
                .then(function(file) {
                    file.width = file.image.width || image.naturalWidth;
                    file.height = file.image.height || image.naturalHeight;

                    if (file.discardImage) {
                        delete file.image;
                        file.image = "removed";
                        delete file.discardImage;
                    }

                    self._imageDimensionsEvent(file);

                    return file;
                });
        }
    }

    /**
     * ASYNC function
     * Read an image from a file object and put the file contents
     * as a content property on the file.
     * Usually this will not load the image becuase the width and height is already fetched via exif data/header of image.
     */
    _readImage(file) {
        return new Promise(function(resolve, reject) {
            file.image = new Image();
            file.image.onload = function() {
                window.URL.revokeObjectURL(this.src);
                resolve(file);
            };
            file.image.onerror = function() {
                reject({
                    file: file,
                    code: ErrorCodes.GET_IMAGE_DATA_FAILED,
                    message: `We couldn’t read the image ‘${file.name}’ so it will not be included in your image collection. This might happen if the file is not an image file (for example a jpeg or png), or because the image is broken. Please check the file and try to re-upload it`
                });
            };
            file.image.src = window.URL.createObjectURL(file);
        });
    };

    /**
     * Is mime type image ?
     * @param file
     * @returns {boolean}
     * @private
     */
    _isImageMimeType(file) {
        return ["image/jpeg", "image/png"].indexOf(file.type) > -1;
    };

    /**
     * ASYNC operation
     * This will return an object that will tell us how to rotate and mirror the
     * image based on exif Orientation value of the image
     * It will also fetch image taken date
     *
     * @param file
     * @returns {Promise<any>}
     * @private
     */
    _getFileData(file) {
        var self = this;
        self.profiling.start("getFileData", file.localId);
        return new Promise(function(resolve, reject) {
            var reader = new FileReader();
            reader.onload = function(e) {
                if (e.target.result) {

                    if (self._isImageMimeType(file)) {
                        try {
                            //Try to get width and height of image using only the header of the image file
                            var resultArray = new Uint8Array(e.target.result);

                            //Get exif data
                            var data = Imageinfo(resultArray);
                            if (data) {
                                file.width = data.width;
                                file.height = data.height;
                            }
                        } catch (e) {
                            //Ignore because we can get the dimensions by loading the image instead (should not happen often)
                        }
                        try {
                            self._applyEXIF(e.target.result, file);
                        } catch (e) {
                            self._uploadWarningHandler(file, {code: WarningCodes.READ_EXIF_FAILED, message: e});
                        }

                        if (file.hasOwnProperty("width") && file.hasOwnProperty("height")) {
                            self._imageDimensionsEvent(file);
                        }
                    }
                }
                reader = undefined; //Memory management
                self.profiling.end("getFileData", file.localId);
                resolve(file);

            };
            reader.onerror = function(e) {
                if (console) {
                    console.error(
                        "Tried reading slice of file for exif and header data, but error occured",
                        e);
                }
                reader = undefined; //Memory management
                resolve(file); //Ignore error because it's not that important if we can't get the exif rotation data, it's just a convenience for the customer
            };
            reader.readAsArrayBuffer(file.slice(0, 131072));
        });
    }

    /**
     * Validate file
     * @param file
     * @returns {Promise<any>}
     * @private
     */
    _validateFile(file) {
        var self = this;
        return new Promise(function(resolve, reject) {

            //Check that the file type is allowed
            if (!(self.allowedMimeTypes.indexOf(file.type) > -1)) {
                reject({
                    file: file,
                    code: ErrorCodes.UNSUPPORTED_FILE_TYPE,
                    message: `The file ‘${file.name}’ has a file type that is not supported (‘${file.type}’).`
                });
                return;
            }

            //Validate the file size
            if (!FileValidation.validateFileSize(file.size, self.maxFileSize)) {
                reject({
                    file: file,
                    code: ErrorCodes.MAX_FILE_SIZE,
                    fileSize: file.size,
                    maxFileSize: self.maxFileSize,
                    message: `The file ‘${file.name}’ is bigger in size than the maximum allowed (‘${self.maxFileSize}’)`
                });
                return;
            }

            resolve(file);
        });
    }

    /**
     * Read the EXIF data and apply onto given file
     *
     * @param {Object} data
     * @param {Object} file
     */
    _applyEXIF(data, file) {

        var exifData = this.exif.getExifData(data);

        //Apply default filter to image
        file.filter = {
            "rotate": {
                "angle": 0,
                "flip": "NONE"
            }
        };

        if (exifData.dateTaken) {
            file.dateTaken = exifData.dateTaken;
        }
        if (exifData.rotate) {
            file.filter.rotate.angle = exifData.rotate;
        }
        if (exifData.flip) {
            file.filter.rotate.flip = exifData.flip;
        }
    }

    /**
     * Update statistics listeners
     * @private
     */
    _updateStatisticsListeners() {
        const statistics = {
            uploaded: this.filesUploaded,
            total: this.filesAdded.length,
            failed: this.filesFailed,
            serverData: this.filesAdded.reduce((total,item) => {
                return total + (item.hasOwnProperty("storageCoreData") ? 1 : 0);
            }, 0)
        };

        // Set interval of retrieving storageCoreData if needed. Inject filesFailedOnStorageCore for correct logic
        this._setGetDataFromStorageApiIntervalIfNeeded({ ...statistics, filesFailedOnStorageCore: this.filesFailedOnStorageCore });

        this._triggerArrayOfListeners(this.statisticsEventListeners, statistics);

        // Check if empty
        if (statistics.total === 0 && statistics.failed === 0 && statistics.uploaded === 0) return;

        //Notify when all uploads are complete
        //No automatic linking will occur at this point automatically because we're getting
        //rid of "native" support of anonymous sessions; linking will be invoked exclusively externally
        if (statistics.total === statistics.uploaded && !this.uploadDone) {
            if (this._validateUploadedFiles()) {
                this._updateUploadDoneListeners();
            }
        }
    }

    /**
     * Validate uploaded files
     * Extra security check just to make sure all the uploads have been through all the upload steps
     * @returns {boolean}
     * @private
     */
    _validateUploadedFiles() {
        if (this.uploadToXServer) return true;
        for (var index = 0; index < this.filesAdded.length; index++) {
            var file = this.filesAdded[index];
            if (!file.hasOwnProperty("uploadError") && !file.hasOwnProperty("alreadyUploaded")) {
                if (!file.hasOwnProperty("regId")) {
                    return false;
                }
                if (!file.hasOwnProperty("uploadData")) {
                    return false;
                }
                if (!file.hasOwnProperty("etag")) {
                    return false;
                }
                if (!file.hasOwnProperty("uploadComplete")) {
                    return false;
                }
            }
        }
        return true;
    }

    /**
     * Update upload done listeners
     * @private
     */
    _updateUploadDoneListeners() {
        this.profiling.end("uploadDone", null);
        this.profiling.report();

        this.uploadDone = true;

        this._triggerArrayOfListeners(this.uploadDoneEventListeners);
    }

    /**
     * Update file added listeners
     * @param file
     * @private
     */
    _updateFileAddedListeners(file) {
        this._triggerArrayOfListeners(this.fileAddedEventListeners, file);
    }

    /**
     * Update file added multiple times listeners
     * @param file
     * @private
     */
    _updateFileAddedMultipleTimesListeners(file) {
        this._triggerArrayOfListeners(this.fileAddedMultipleTimesEventListeners, file);
    }

    /**
     * Update upload start listeners
     * @param file
     * @private
     */
    _updateUploadStartListeners(file) {
        this._triggerArrayOfListeners(this.uploadStartEventListeners, file);
    }

    /**
     * Update upload complete listeners
     * @param file
     * @private
     */
    _updateUploadCompleteListeners(file) {
        this._triggerArrayOfListeners(this.uploadCompleteEventListeners, file);
    }

    /**
     * Update file resitered listeners
     * @param file
     * @private
     */
    _updateFileRegisteredListeners(file) {
        this._triggerArrayOfListeners(this.fileRegistrationEventListeners, file);
    }

    /**
     * Update storage core data received listeners
     * @param file
     * @private
     */
    _updateStorageCoreDataRecievedListeners(file) {
        const update = () => {
            this._triggerArrayOfListeners(this.storageCoreDataRecievedListeners, file);
            this._updateStatisticsListeners();
        }

        if (this.uploadToXServer) {
            this._waitForThumbnails(file.storageCoreData.thumbnails)
                .catch(error => console.error(error))
                .then(() => update())
        } else {
            update()
        }
    }

    /**
     * Update file uploadcanceled listeners
     * @param file
     * @private
     */
    _updateUploadCanceledListeners(file) {
        this._triggerArrayOfListeners(this.uploadCanceledEventListeners, file);
    }

    /**
     * Upload error handler
     * @param {Object} fileToUpload
     * @param {Object} error
     * @private
     */
    _uploadErrorHandler(fileToUpload, error) {
        // Remove the failed file from all lists and update statistics
        this._discardFile(fileToUpload);
        
        if (error.error && error.error.code) {
            switch (error.error.code) {
                case ErrorCodes.CANCELLED:
                case ErrorCodes.DUPLICATE:
                    // Do not process duplicate/cancellation errors further
                    this._updateStatisticsListeners();
                    return;
                case 'Empty file':
                    error.error.code = ErrorCodes.EMPTY_FILE;
                    break;
            }
        }

        if (fileToUpload) {
            fileToUpload.uploadError = error;
        }

        //Increment the filesFailed counter
        this.filesFailed++;

        //Notify the listeners of the upload error
        this._triggerArrayOfListeners(this.uploadErrorEventListeners, fileToUpload, error);

        this._updateStatisticsListeners();
    }

    _triggerArrayOfListeners(listenerArray, ...args) {
        listenerArray.forEach(listener => {
            try {
                listener(...args)
            } catch (err) {
                // Silent error, because we don't care if listeners fail
            }
        });
    }

    /**
     * Handle upload warnings
     * @param {Object} fileToUpload
     * @param {Object} warning
     */
    _uploadWarningHandler(fileToUpload, warning) {
        this.uploadWarningEventListeners.forEach (function (callback) {
            callback(fileToUpload, warning);
        });
    }

    /**
     * Upload progress event
     * @param file
     * @param {Object} data
     * @private
     */
    _uploadProgressEvent(file, data) {
        var percent = Math.round((data.loaded / data.total) * 100);

        this._triggerArrayOfListeners(this.fileUploadProgressEventListeners, file, percent);
    }

    /**
     * Upload retry event
     * @param file
     * @param number count
     * @private
     */
    _uploadRetryEvent(file, count) {
        this._triggerArrayOfListeners(this.fileUploadRetryEventListeners, file, count);
    }

    /**
     * Send out an imageDimensions event to notify that image dimensions is now aquired and available
     * @param {File} file
     */
    _imageDimensionsEvent(file) {
        this._triggerArrayOfListeners(this.imageDimensionsEventListeners, file);
    }

    /***
     * Upload
     * @private
     * @param file File that is going to be uploaded to amazon. already registered with albelli
     * @returns {axios.Promise}
     */
    _upload(file) {
        try {
            this._throwIfEmptyFile(file);
        } catch (err) {
            return Promise.reject({
                error: {
                    error: "Empty file",
                    code: ErrorCodes.EMPTY_FILE
                }
            });
        }

        this.profiling.start("upload", file.localId);

        // If the file is marked for cancellation, reject immediatley
        const fileUploadToken = file.cancelTokenList.find((item) => item.name === 'fileUpload');
        if (fileUploadToken) {
            try {
                fileUploadToken.cancelToken.throwIfCanceled();
            } catch (err) {
                return Promise.reject({
                    error: {
                        error: "Cancelled",
                        code: ErrorCodes.CANCELLED
                    }
                });
            }
        }

        this._updateUploadStartListeners(file);

        if (this.uploadToXServer) {
            return new Promise((resolve, reject) => {
                const data = this.xserverApiClient.uploadFile(
                    file.uploadData.uploadUrl,
                    file.uploadData.postParameters,
                    file,
                    (f, d) => {
                        this._uploadProgressEvent(f, d)
                    }
                );
                file.cancelTokenList.push({
                    name: 'fileUploadAjax',
                    cancelToken: data.cancelToken
                });
                data.promise.then(() => {
                    this.profiling.end("upload", file.localId);
                    resolve(file);
                }).catch((error) => {
                    reject({
                        message: `We’re sorry: we couldn’t registrer file ‘${file.name}’, so we’ve removed it from your order. This might be a temporary error and you could try uploading it again`,
                        error: {
                            error: error
                        },
                        code: this._getUploadErrorCode(error.subMessage)
                    });
                });
            });
        }
        else {
            return new Promise((resolve, reject) => {
                const data = this.storageApiClient.upload(
                    file.uploadData.url,
                    file,
                    (f, d) => {
                        this._uploadProgressEvent(f, d)
                    },
                    file.base64md5Checksum,
                    (f, d) => {
                        this._uploadRetryEvent(f, d)
                    }
                );
                file.cancelTokenList.push({
                    name: 'fileUploadAjax',
                    cancelToken: data.cancelToken
                });
                data.promise.then((etag) => {
                    file.etag = etag;
                    this.profiling.end("upload", file.localId);
                    resolve(file);
                }).catch((error) => {
                    reject({
                        message: `We’re sorry: we couldn’t registrer file ‘${file.name}’, so we’ve removed it from your order. This might be a temporary error and you could try uploading it again`,
                        error: {
                            error: error
                        },
                        code: this._getUploadErrorCode(error.subMessage)
                    });
                });
            })
        }
    }

    /**
     * Get upload error code
     * @param {string} message
     * @returns {string}
     * @private
     */
    _getUploadErrorCode(message) {
        var errorCode = ErrorCodes.UPLOAD_FAILED;
        switch (message) {
            case 'Cancelled':
                errorCode = ErrorCodes.CANCELLED;
            break;
            case 'Empty File':
                errorCode = ErrorCodes.EMPTY_FILE;
            break;
        }
        return errorCode;
    }

    /**
     * When the upload is done, we need to set an upload complete status
     * on a webservice. This function will do that.
     *
     * @param {Object} file
     */
    _setUploadCompletedStatus(file) {
        var self = this;
        if (file.alreadyUploaded) {
            return Promise.resolve(file);
        }
        self.profiling.start("uploadComplete", file.localId);
        return new Promise(function(resolve, reject) {
            if (self.uploadToXServer) {
                self.xserverApiClient.setUploadCompletedStatus(file.regId)
                    .then(function() {
                        self.profiling.end("uploadComplete", file.localId);
                        file.uploadComplete = true;
                        resolve(file);
                    }).catch(function(error) {
                        reject({
                            message: `We’re sorry: we couldn’t registrer file ‘${file.name}’, so we’ve removed it from your order. This might be a temporary error and you could try uploading it again`,
                            error: error,
                            file: file,
                            code: ErrorCodes.COMPLETE_UPLOAD_FAILED
                        });
                    })
            }
            else {
                self.storageApiClient.completeUpload(file.regId, file.etag, file.uploadedAnonymously)
                    .then(function() {
                        self.profiling.end("uploadComplete", file.localId);
                        file.uploadComplete = true;
                        resolve(file);
                    }).catch(function(error) {
                        reject({
                            message: `We’re sorry: we couldn’t registrer file ‘${file.name}’, so we’ve removed it from your order. This might be a temporary error and you could try uploading it again`,
                            error: error,
                            file: file,
                            code: ErrorCodes.COMPLETE_UPLOAD_FAILED
                        });
                    })
            }
        });
    }

    /**
     * After images have been uploaded, the server will make thumbnails.
     * Fetch these when they are ready
     */
    _getDataFromStorageApi() {
        // Get files to poll to get storage core data
        let filesToPoll = this.filesRegistered.filter(file => !file.cancelled && !file.hasOwnProperty("storageCoreData") && !file.hasOwnProperty("uploadError") && file.hasOwnProperty("uploadComplete"));

        // Partition filesToPoll into two arrays, one which exceeds number of retries and needs to be marked as failed and second, which needs to be followed up
        const [filesExceededStorageCoreRetries, filesNeedMoreStorageCoreRetries] = Utils.partition(filesToPoll, file => (file.storageCoreAttempts && file.storageCoreAttempts > STORAGE_CORE_DATA_ATTEMPTS));
        
        filesToPoll = filesNeedMoreStorageCoreRetries;

        filesToPoll.forEach(file => {
            file.storageCoreAttempts = (file.storageCoreAttempts ? file.storageCoreAttempts : 0) + 1;
        });

        // Mark files as failed
        filesExceededStorageCoreRetries.forEach(file => {
            const error = new Error("Exceeded maximum amount of storage core data requests");
            error.code = ErrorCodes.STORAGE_CORE_RETRIEVE_FAILED;
            this.filesFailedOnStorageCore++;
            this._uploadErrorHandler(file, error);
        });

        // If no files to poll
        if (filesToPoll.length === 0) return;
        
        const [anonymousUploadedFiles, authenticatedUploadedFiles] = Utils.partition(filesToPoll, item => item.uploadedAnonymously)

        if (anonymousUploadedFiles.length > 0) {
            this._storageCoreDataRequest(anonymousUploadedFiles.map(f => f.regId), true);
        }

        if (authenticatedUploadedFiles.length > 0) {
            this._storageCoreDataRequest(authenticatedUploadedFiles.map(f => f.regId), false);
        }
    };

    /**
     * Set interval of _getDataFromStorageApi if any of the uploaded images still need storageCoreData
     * @param {object} statistics
     */
    _setGetDataFromStorageApiIntervalIfNeeded(statistics) {
        if (statistics.uploaded > (statistics.serverData + statistics.filesFailedOnStorageCore)) { // Need to retrieve data from storage api
            if (!this.storageCoreInterval) {
                this.storageCoreInterval = setInterval(() => {
                    // adding this flag coz requesting storage core data is async
                    // we have to be sure not to fire request again while the async function is still running
                    // TODO: the interval request should always fire after receiving response. Hence this method
                    // should be change into recursive async method when interval is fired only after receiving response
                    if (!this.isStorageCoreDataRequestRunning) {
                        this._getDataFromStorageApi();
                    }
                }, STORAGE_CORE_DATA_INTERVAL_TIME);
            }
        } else {
            if (this.storageCoreInterval) {
                clearInterval(this.storageCoreInterval);
                this.storageCoreInterval = null;
            }
        }
    }

    /**
     * Storage core data request
     * @param fileList
     * @param {boolean} isAnonymous
     * @private
     */
    _storageCoreDataRequest(fileList, isAnonymous) {
        var self = this;
        // set flag so interval won't be fired while this function is still running
        self.isStorageCoreDataRequestRunning = true;
        this.storageApiClient.getUploadData(fileList, isAnonymous)
            .then(function(imageList) {
                self.isStorageCoreDataRequestRunning = false;
                for (var i = 0; i < imageList.length; i++) {
                    const file = self.filesRegistered.find(x => x.regId === imageList[i].imageId);
                    if (!file) continue;  // TODO: why would that happen? what to do when it actually happens?
                    const storageCoreData = imageList[i];
                    if (self._isStorageCoreDataValid({storageCoreData, storageFileType: file["storageFileType"]})) {
                        file.storageCoreData = storageCoreData;
                        self._updateStorageCoreDataRecievedListeners(file);
                    }
                }
            }).catch(function(error) {
                self.isStorageCoreDataRequestRunning = false;
                fileList.map(function (item) {
                    // Commented below line because item is a string and it will always throw error
                    //item.storageCoreData = "FAILED TO GET STORAGE CORE DATA. Error: " + error;
                    console.warn(error);
                });
            });
    }

    /**
     * @private
     * @param {{storageCoreData:Object, storageFileType: string}}} param 
     * @return {boolean}
     */
    _isStorageCoreDataValid({storageCoreData, storageFileType}) {
        if (!storageCoreData) return false;
        if (storageFileType === "ProductPreviewImage") return true;
        return storageCoreData.thumbnails && storageCoreData.thumbnails.length > 0;
    }

    /**
     * Create filter-rotated url
     * @param {string} url
     * @param filter
     * @returns {*}
     * @private
     */
    _createFilterRotatedUrl(url, filter) {
        if (Utils.emptyVariable(filter)) {
            return url;
        }
        return this.imageEditServerUrl +
            "/api/EditGet/editByUrlEncodedOperation?operation=" + btoa(JSON.stringify(filter)) +
            "&url=" + encodeURIComponent(url);
    }
    
    /**
     * Cleanup file from additional context properties that library is adding for clean('ish) reupload
     * @param {File} file file to import
     * @returns {File} clean file
     * @public
     */
    cleanPhotoFileForReupload(file) {
        delete file.cancel;
        delete file.cancelTokenList;
        delete file.cancelled;
        delete file.etag;
        delete file.regId;
        delete file.uploadComplete;
        delete file.uploadData;
        delete file.uploadError;
        delete file.storageCoreAttempts;

        return file;
    }
}

export {
    Uploader, ErrorCodes, WarningCodes
};
